import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { palette } from '../../design-system/colors';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { TextDefault } from '../../design-system/texts';
import { isMobile } from 'react-device-detect';
import { FlexDiv } from '../../design-system/containers';
import { isHighlightOnlyAnswer } from '../answer-highlight';

const useStyles = makeStyles(() => ({
    toggleButtonStyles: {
        marginRight: '1px',
        transition: 'ease 0.2s all',
        flexGrow: 1,
        border: '1px solid #D2D2D2',
        borderRadius: '4px',

        '&:focus': {
            outline: 'none',
        },
    },

    buttonStyles: {
        padding: '12px 40px',
        margin: '0 2px',
        background: palette.graphite.white,
        boxShadow: '0px 4px 8px rgba(29, 30, 29, 0.06)',
        borderRadius: '4px',
        transition: 'ease 0.2s all',
        textTransform: 'none',
        border: '2px solid transparent',
        whiteSpace: 'nowrap',

        '@media (max-width: 992px)': {
            marginRight: '0px',
            marginTop: '20px',
            width: '70%',

            fontSize: '14px',
            fontWeight: 700,
            margin: '5px',
        },

        '&:hover': {
            boxShadow:
                '0px 0px 2px rgba(0, 0, 0, 0.08), 0px 4px 24px rgba(29, 30, 29, 0.08)',
        },
        '&:active': {
            boxShadow: '0px 0px 4px rgba(29, 30, 29, 0.08)',
        },
        '&.MuiToggleButtonGroup-grouped': {
            marginRight: '10px',
            borderRadius: '4px',
        },
    },

    isSelected: {
        '&.Mui-selected': {
            backgroundColor: palette.graphite.heavyMetal,
            '&:hover': {
                backgroundColor: palette.lightroom.heavyMetalLight,
            },
            '&:active': {
                border: '2px solid',
                borderColor: palette.lightroom.heavyMetalLight,
                background: palette.graphite.heavyMetal,
            },
        },
    },

    buttonIcon: {
        content: '',
        position: 'absolute',
        left: '10px',
        width: '20px',
        height: '20px',
    },

    correctIcon: {
        background:
            'url(/static/img/assessment/icon-correct.svg) no-repeat center center',
    },

    incorrectIcon: {
        background:
            'url(/static/img/assessment/icon-incorrect.svg) no-repeat center center',
    },

    equalWidth: {
        flexBasis: (props) =>
            props.oneOf && Math.round(100 / props.oneOf) + '%',
        flexGrow: 0,
    },

    fullWidthGroup: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-evenly',
        marginBottom: '20px',
        gap: '16px',
        flexWrap: (props) => (props.isMobile ? 'wrap' : 'nowrap'),
    },

    isUserCorrect: {
        backgroundColor: palette.lightroom.aspargusLightHigh,
        borderColor: palette.evergreen.aspargus,
        borderWidth: '2px',
    },
    isIncorrect: {
        backgroundColor: palette.lightroom.redLightHigh,
        borderColor: palette.vibrant.red,
        borderWidth: '2px',
    },
}));

export function AnswerableToggleButtonGroup({
    value,
    handleToggle,
    fullWidth,
    children,
    ...props
}) {
    const classes = useStyles();

    return (
        <ToggleButtonGroup
            className={classnames({ [classes.fullWidthGroup]: fullWidth })}
            onChange={handleToggle}
            value={value}
            exclusive
            aria-label="text alignment"
            {...props}
        >
            {children}
        </ToggleButtonGroup>
    );
}

export function AnswerableToggleButton({
    key,
    value,
    text,
    answer,
    handleToggle,
    selected,
    oneOf,
}) {
    const classes = useStyles({ oneOf, isMobile });

    const highlightOnly = isHighlightOnlyAnswer(answer);

    const isCorrect = () => {
        return answer && answer.correctAnswer[value];
    };

    const isUserCorrect = () => {
        if (highlightOnly) {
            return isCorrect();
        }
        return (
            answer &&
            answer.correctAnswer[value] &&
            answer.userAnswer.value === value
        );
    };

    const isIncorrect = () => {
        if (highlightOnly) {
            return false;
        }
        return (
            answer &&
            !answer.correctAnswer[value] &&
            answer.userAnswer.value === value &&
            answer.userAnswer.score === 0
        );
    };

    const getAnswerColor = (isCorrect, isIncorrect, isSelected) => {
        if (isCorrect) {
            return palette.evergreen.aspargus;
        } else if (isIncorrect) {
            return palette.vibrant.red;
        } else if (isSelected) {
            return palette.graphite.white;
        } else {
            return palette.graphite.heavyMetal;
        }
    };

    return (
        <ToggleButton
            className={classnames(
                classes.buttonStyles,
                classes.toggleButtonStyles,
                {
                    'Mui-disabled': !!answer,
                    [classes.isCorrect]: isCorrect(),
                    [classes.isUserCorrect]: isUserCorrect(),
                    [classes.isIncorrect]: isIncorrect(),
                    [classes.toggleButtonHover]: !isMobile,
                    [classes.equalWidth]: oneOf && oneOf > 0,
                    [classes.isSelected]: selected,
                }
            )}
            key={key}
            aria-label={value}
            value={value}
            selected={selected}
            onClick={!answer && handleToggle}
        >
            {(isCorrect() || isIncorrect()) && ( // Sometimes we display an Icon
                <FlexDiv
                    className={classnames(classes.buttonIcon, {
                        [classes.correctIcon]: isCorrect(),
                        [classes.incorrectIcon]: isIncorrect(),
                    })}
                />
            )}
            <TextDefault
                bold
                color={getAnswerColor(isUserCorrect(), isIncorrect(), selected)}
            >
                {text}
            </TextDefault>
        </ToggleButton>
    );
}
